import { useState, useMemo, ComponentType } from 'react'
import Layout from 'shared/layouts/containers/Main'
import useInit from './hooks/useInit'
import DeleteModal from 'shared/components/DeleteModal'
import { apiUrls, tableColumnWidths, roles } from 'consts'
import getRows from './utils/getRows'
import {
  SearchState,
  IntegratedFiltering,
  SortingState,
} from '@devexpress/dx-react-grid'
import {
  Grid,
  SearchPanel,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  Toolbar,
} from '@devexpress/dx-react-grid-bootstrap4'
import { TableColumnWidthInfo } from '@devexpress/dx-react-grid/dist/dx-react-grid'
import LookupTablesFilter from 'components/LookupTablesFilter'
import reorderRows from './utils/reorderRows'
import {
  DndContext,
  closestCenter,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import SortableItem from 'components/SortableItem'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import SortableTableCell from 'components/SortableTableCell'
import SortableOverLayContent from 'components/SortableOverlayContent'
import useReorder from 'hooks/useReorder'
import HeaderCustomerSelect from 'containers/HeaderCustomerSelect'
import getBrandContent from 'utils/getBrandContent'
import SearchPanelInput from 'shared/components/SearchPanelInput'
import Footer from 'shared/components/Footer'
import showCustomerSwitcher from 'utils/showCustomerSwitcher'

const Carriers: React.FunctionComponent = () => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [rows, setRows] = useState<any>([])
  const [showModal, setShowModal] = useState<boolean>(false)
  const [deleteEntryId, setDeleteEntryId] = useState<boolean | number>(false)
  const [dragEndEvent, setDragEndEvent] = useState<any | null>(null)
  const sortableItems = useMemo(() => rows?.map(({ id }) => id), [rows])

  const columns: { name: string; title: string; encodeHtml?: boolean; getCellValue?: any }[] = [
    { name: 'sortColumn', title: ' ' },
    { name: 'order', title: '#' },
    { name: 'id', title: 'ID' },
    { name: 'name', title: 'Name' },
    { name: 'show', title: 'Show' },
    { name: 'action', title: 'Actions', encodeHtml: false },
  ]

  const columnWidths: TableColumnWidthInfo[] = [
    { columnName: 'sortColumn', width: tableColumnWidths.sortColumn },
    { columnName: 'order', width: tableColumnWidths.sortColumn },
    { columnName: 'id', width: tableColumnWidths.id },
    { columnName: 'name', width: 'auto' },
    { columnName: 'show', width: tableColumnWidths.show },
    { columnName: 'action', width: tableColumnWidths.action },
  ]

  const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})

  useInit({
    getRows,
    setRows,
    setDeleteEntryId,
    setShowModal,
    setShowOverlay,
  })

  useReorder({
    rows,
    dragEndEvent,
    setRows,
    reorderRows,
  })

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}))

  const TableRow: ComponentType<any> | undefined = ({ row, ...restProps }) => {
    return (
      <SortableItem key={rows.indexOf(row)} index={rows.indexOf(row)} row={row} {...restProps} />
    )
  }

  const TableCell: any = ({ value, ...rest }) => {
    return (
      <SortableTableCell value={value} tableCellProps={rest} draggableColumnName='sortColumn' />
    )
  }

  const TableComponent = ({ ...restProps }) => (
    <Table.Table {...restProps} className='with-action min-mobile-width-xs' />
  )

  const SearchPanelInputComponent = (props) => {
    return SearchPanelInput({
      addUrl: '/add-carrier',
      addText: 'Add Carrier',
      inputProps: props,
      insert: <LookupTablesFilter appUrl='carriers' />,
    })
  }

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showMinimizedBarButton={true}
      showOverlay={showOverlay}
      headerInsert={
        showCustomerSwitcher() ? (
          <HeaderCustomerSelect
            setShowOverlay={setShowOverlay}
            handleCustomerChanged={(customer) => {
              setCurrentCustomer(customer)
              setShowOverlay(false)
            }}
          />
        ) : undefined
      }
    >
      <>
        <DndContext
          sensors={sensors}
          onDragEnd={(e) => setDragEndEvent(e)}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
            <Grid rows={rows} columns={columns}>
              <SortingState defaultSorting={[{ columnName: 'sortColumn', direction: 'asc' }]} />
              <SearchState defaultValue='' />
              <IntegratedFiltering />
              <Toolbar />
              <SearchPanel inputComponent={SearchPanelInputComponent} />
              <Table
                tableComponent={TableComponent}
                cellComponent={TableCell}
                rowComponent={TableRow}
              />
              <TableColumnResizing defaultColumnWidths={columnWidths} resizingMode='nextColumn' />
              <TableHeaderRow />
            </Grid>

            <DeleteModal
              url={apiUrls.carriers}
              showModal={showModal}
              setShowModal={setShowModal}
              deleteEntryId={deleteEntryId}
              setDeleteEntryId={setDeleteEntryId}
              entityName='Carrier'
              getRows={getRows}
              setRows={setRows}
            />
          </SortableContext>
          <DragOverlay>
            <SortableOverLayContent />
          </DragOverlay>
        </DndContext>
      </>
    </Layout>
  )
}

export default Carriers
